import { AfterViewInit, Component, ElementRef, inject, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { CustomReactionImageAnimationSettings } from '../models/reaction-type.model';
import { fromEvent, merge, Subject, takeUntil, tap } from 'rxjs';

export type ReactionRelativeFromElement = string | HTMLElement | ElementRef<HTMLElement>;

@Component({
  selector: 'svc-reaction-animation',
  templateUrl: './svc-reaction-animation.component.html',
  styleUrls: ['./svc-reaction-animation.component.scss'],
  host: {
    '[class]': `relativeToExternalElement ? 'fixed' : 'absolute -translate-x-1/2 -translate-y-1/2'`,
    '[style.width.px]': `reactionAnimation?.sizeWidth`,
    '[style.height.px]': `reactionAnimation?.sizeHeight`,
    '[style.left]': `positions.left + (relativeToExternalElement ? 'px' : '%')`,
    '[style.top]': `positions.top + (relativeToExternalElement ? 'px' : '%')`,
  },
})
export class SvcReactionAnimationComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public reactionAnimation!: CustomReactionImageAnimationSettings;
  @Input('relativeFromElement') public set setRelativeFromElement(value: ReactionRelativeFromElement | ReactionRelativeFromElement[]) {
    this.relativeFromElement = value ? (Array.isArray(value) ? value : [value]) : null;
  }

  #ngZone = inject(NgZone);
  #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  #destroy$: Subject<void>;

  get #element(): HTMLElement {
    return this.#elementRef?.nativeElement;
  }
  get #parentElement(): HTMLElement {
    return this.#element?.parentElement;
  }
  get #externalElement(): HTMLElement[] {
    return (
      this.relativeFromElement?.reduce((list, item) => {
        let element: HTMLElement;
        if (item) {
          if (item instanceof ElementRef && item.nativeElement) element = item.nativeElement;
          else if (typeof item == 'string') element = document.querySelector(item);
          else element = item as HTMLElement;
        }
        if (element) list.push(element);
        return list;
      }, []) ?? []
    );
  }

  protected relativeToExternalElement: boolean = false;
  protected positions = { left: 0, top: 0 };
  protected relativeFromElement: ReactionRelativeFromElement[];

  public ngOnChanges(): void {
    this.#preparePositionsAndMeasures();
  }

  public ngAfterViewInit(): void {
    this.#preparePositionsAndMeasures();
  }

  #preparePositionsAndMeasures() {
    if (!this.reactionAnimation) return;

    this.relativeToExternalElement = (this.#externalElement ?? [])?.length > 0;

    if (this.relativeToExternalElement) {
      const updatePositions = (): void => {
        const reactionElement = this.#parentElement;
        const halfImageWidth = (this.reactionAnimation?.sizeWidth ?? 0) * 0.5;
        const imageHeight = (this.reactionAnimation?.sizeHeight ?? 0) * 0.5;
        const reactionRect = reactionElement?.getBoundingClientRect();
        this.positions = {
          left: reactionElement ? reactionRect.x + reactionRect.width * 0.5 - halfImageWidth : 0,
          top: reactionElement ? reactionRect.y + reactionRect.height * 0.5 - imageHeight : 0,
        };
      };
      updatePositions();
      this.#stopListenings();
      merge(...[...this.#externalElement.map((element) => [fromEvent(element, 'scroll'), fromEvent(element, 'resize')]).flatMap((list) => list), fromEvent(window, 'resize')])
        .pipe(
          takeUntil(this.#destroy$),
          tap(() => this.#ngZone.run(() => updatePositions()))
        )
        .subscribe();
    } else {
      this.positions = {
        left: 50,
        top: 50,
      };
    }
  }

  #stopListenings(options?: { newInstance?: boolean }) {
    this.#destroy$?.next();
    this.#destroy$?.complete();
    if (options?.newInstance ?? true) {
      this.#destroy$ = new Subject();
    }
  }

  public ngOnDestroy(): void {
    this.#stopListenings({ newInstance: false });
  }
}
